"use client";

import { hasAppAccess } from "@pex/database/queries/app-access";
import type { AppContextType } from "@pex/types";
import { useState } from "react";
import { useAuth } from "./use-auth";

/**
 * Reads the current user's app access. The list is resolved once per user in
 * AuthProvider (not per consumer mount), so mounting this hook on every page
 * no longer re-runs the getUserApps queries (#449). `hasAccess` remains a
 * live, on-demand check for callers that need a specific context verified.
 */
export function useAppAccess() {
  const { user, supabase, userApps, appsLoading } = useAuth();
  const [error, setError] = useState<Error | null>(null);

  const checkAccess = async (appContext: AppContextType): Promise<boolean> => {
    if (!user) {
      return false;
    }
    try {
      return await hasAppAccess(supabase, user.id, appContext);
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error("Failed to check app access")
      );
      return false;
    }
  };

  return {
    userApps,
    loading: appsLoading,
    error,
    hasAccess: checkAccess,
  };
}
